import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import ConfirmModal from './ConfirmModal';
import HistoryTimeline from './HistoryTimeline';
import TagsSelect from './TagsSelect';
import { loadTagRules } from '../slices/thunk';
import { triggerSaveSuccessNotif, triggerErrorNotif } from '../slices/notifications/thunk';
import { APIClient } from "../helpers/api_helper";
import MoneyDisplay from './MoneyDisplay';
import utils from './utils';
import { CloseOutlined, DeleteOutlined } from '@ant-design/icons';
import { Alert, Button, Checkbox, Col, Drawer, Empty, Form,
  Input, Row, Segmented, Space, Spin, Switch, Table, Tag, Typography
} from 'antd';

const api = new APIClient();

// The preview fires as you type, so it is debounced. Same feel as the header
// search box, which uses 750ms for the same reason.
const PREVIEW_DEBOUNCE_MS = 600;
// Mirrors MIN_PATTERN_LENGTH in backend/core/api_views/tag_rules.py: below this
// the pattern matches most of everything and previewing it is just noise.
const MIN_PATTERN_LENGTH = 2;


const TagRuleDrawer = (props) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const [activeDrawerTab, setActiveDrawerTab] = useState('data');
  const [showDeletionConfirmModal, setShowDeletionConfirmModal] = useState(false);
  const [data, setData] = useState(null);

  // What this pattern matches TODAY, so the rule can be judged before saving.
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [applyToPast, setApplyToPast] = useState(false);
  const [overwriteTagged, setOverwriteTagged] = useState(false);
  const [applying, setApplying] = useState(false);

  const pattern = Form.useWatch('pattern', form);
  const tagValue = Form.useWatch('tag', form);

  const isCreation = props.id == null;

  // Keyed on props.open, NOT props.isOpen: every call site passes `open`, so an
  // isOpen dep (as the older drawers use) would silently never fire.
  useEffect(() => {
    setActiveDrawerTab('data');
    if (props.id) {
      try {
        api.get(`/api/tag_rules/${props.id}/`, {})
          .then(resp => {
            setData(resp);
            form.setFieldsValue(resp);
          });
      } catch (error) {
        console.error(error);
      }
    } else {
      form.resetFields();
      setData(null);
      // Creation from the transaction drawer arrives prefilled with that
      // transaction's title and tag; the pattern is editable because the raw
      // title ("PETROL Ljubljana 12.3.2026") is rarely the rule you want.
      if (props.open && props.defaultValues) {
        form.setFieldsValue(props.defaultValues);
      }
    }
  }, [props.open, props.id]);

  // Reset the backfill choices per drawer session. They are deliberately NOT
  // sticky: "also retag everything that already matches" is destructive and
  // must be re-asked for each rule, never inherited from the last one.
  useEffect(() => {
    setApplyToPast(false);
    setOverwriteTagged(false);
    setPreview(null);
  }, [props.open, props.id]);

  // Debounced preview. Re-runs on the tag too, because the counts split on
  // "already carries THIS tag" vs "carries a different one".
  useEffect(() => {
    if (!props.open) return undefined;
    const trimmed = (pattern || '').trim();
    if (trimmed.length < MIN_PATTERN_LENGTH) {
      setPreview(null);
      setPreviewLoading(false);
      return undefined;
    }
    setPreviewLoading(true);
    const timer = setTimeout(() => {
      api.get('/api/tag_rules/preview/', { pattern: trimmed, tag: tagValue })
        // 403 resolves to null instead of rejecting (see api_helper).
        .then((resp) => setPreview(resp || null))
        .catch(() => setPreview(null))
        .finally(() => setPreviewLoading(false));
    }, PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [pattern, tagValue, props.open]);


  const handleCancel = () => {
    form.resetFields();
    props.onCancel()
  }

  const handleConfirmedDelete = () => {
    api.delete(`/api/tag_rules/${props.id}/`, {}, {successMsg: `Pravilo uspešno izbrisano`})
      .then(resp => {
        dispatch(loadTagRules());  // the drawer matcher reads this list
        props.onDone()
        form.resetFields();
        setShowDeletionConfirmModal(false);
      })
      .catch(error => {
        console.error(error)
      })
  };

  const onSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        const isEdit = props.id !== null && props.id !== undefined;
        const apiCall = isEdit
            ? api.update(`/api/tag_rules/${props.id}/`, values)
            : api.create('/api/tag_rules/', values, {successMsg: `Pravilo uspešno ustvarjeno`});

        apiCall.then(resp => {
            if (!resp?.id) return;   // 403 resolves to null instead of rejecting
            // Backfill is a SECOND call, made only after the rule saved: a rule
            // that failed validation must never retag anything, and keeping it
            // separate means the same endpoint can rerun for an existing rule.
            const done = () => {
              dispatch(loadTagRules());  // the drawer matcher reads this list
              props.onDone();
              form.resetFields();
            };
            if (!applyToPast) return done();

            setApplying(true);
            api.post(
              `/api/tag_rules/${resp.id}/apply/`,
              { overwrite: overwriteTagged },
              { successMsg: null }   // we word our own, with the row count
            )
              .then((applyResp) => {
                const n = applyResp?.applied ?? 0;
                dispatch(triggerSaveSuccessNotif({
                  successMsg: n
                    ? `Pravilo uporabljeno za ${n} obstoječih transakcij.`
                    : 'Ni bilo obstoječih transakcij za posodobitev.',
                }));
              })
              // The rule itself is saved either way, so a failed backfill is
              // reported but must not look like the save failed.
              .catch((err) => {
                console.error('Backfill failed:', err);
                dispatch(triggerErrorNotif(
                  'Pravilo je shranjeno, uporaba za obstoječe transakcije pa ni uspela.'
                ));
              })
              .finally(() => {
                setApplying(false);
                done();
              });
          }).catch(err => {
            // The duplicate-pattern check is field-keyed on the server precisely
            // so it can land on the pattern input rather than a bare toast.
            const respData = err?.response?.data;
            if (respData && typeof respData === 'object' && !Array.isArray(respData)) {
              form.setFields(
                Object.entries(respData).map(([name, errors]) => ({
                  name,
                  errors: Array.isArray(errors) ? errors : [String(errors)],
                }))
              );
            }
            console.error('API error:', err);
          });
      })
      .catch((info) => {
        console.error('Validate Failed:', info);
      });
  };

  // How many rows the backfill would actually change, which is NOT the match
  // count: matches already carrying this tag are nothing to do, and the ones on
  // a different tag only count when overwrite is on.
  const willTag = (preview?.untagged ?? 0) + (overwriteTagged ? (preview?.tagged_other ?? 0) : 0);

  const previewColumns = [
    {
      title: 'Naziv', dataIndex: 'title', ellipsis: true,
      render: (title) => <span title={title}>{title}</span>,
    },
    {
      title: 'Datum', dataIndex: 'date_to_be_paid', width: 110,
      render: (d) => utils.formatDt(d),
    },
    {
      title: 'Znesek', dataIndex: 'amount', width: 110, align: 'right',
      render: (amount, r) => <MoneyDisplay type={r.type} val={amount} />,
    },
    {
      title: 'Trenutna oznaka', dataIndex: 'tag_title', width: 150,
      render: (t) => (t ? <Tag>{t}</Tag> : <Typography.Text type="secondary">-</Typography.Text>),
    },
  ];

  const previewSection = (
    <>
      <Typography.Title level={5} style={{ marginTop: 8 }}>Ujemanja</Typography.Title>

      {(pattern || '').trim().length < MIN_PATTERN_LENGTH ? (
        <Typography.Text type="secondary">
          Vnesite vzorec, da vidite, katere transakcije se ujemajo.
        </Typography.Text>
      ) : previewLoading && !preview ? (
        <Spin />
      ) : !preview || preview.total === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Nobena obstoječa transakcija se ne ujema"
        />
      ) : (
        <Spin spinning={previewLoading}>
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            <Space size={6} wrap>
              <Tag color="blue">{`Ujemanj: ${preview.total}`}</Tag>
              <Tag>{`brez oznake: ${preview.untagged}`}</Tag>
              {preview.already_correct > 0 && (
                <Tag color="green">{`že pravilno: ${preview.already_correct}`}</Tag>
              )}
              {preview.tagged_other > 0 && (
                <Tag color="orange">{`druga oznaka: ${preview.tagged_other}`}</Tag>
              )}
            </Space>

            <Table
              size="small"
              columns={previewColumns}
              dataSource={preview.sample || []}
              rowKey={(r) => r.id}
              pagination={false}
              scroll={{ y: 240 }}
            />
            {preview.total > (preview.sample?.length ?? 0) && (
              <Typography.Text type="secondary">
                {`Prikazanih ${preview.sample.length} od ${preview.total} ujemanj.`}
              </Typography.Text>
            )}

            <Checkbox
              checked={applyToPast}
              onChange={(e) => {
                setApplyToPast(e.target.checked);
                // Un-ticking the parent must not leave the destructive child
                // armed for the next time it is ticked.
                if (!e.target.checked) setOverwriteTagged(false);
              }}
            >
              {`Uporabi tudi za obstoječe transakcije (${willTag})`}
            </Checkbox>

            {/* Only offered once the backfill itself is on, and only when there
                is actually something tagged differently to overwrite. */}
            {applyToPast && preview.tagged_other > 0 && (
              <div style={{ paddingLeft: 24 }}>
                <Checkbox
                  checked={overwriteTagged}
                  onChange={(e) => setOverwriteTagged(e.target.checked)}
                >
                  {`Prepiši tudi že označene (+${preview.tagged_other})`}
                </Checkbox>
              </div>
            )}

            {applyToPast && overwriteTagged && (
              <Alert
                type="warning"
                showIcon
                message={`${preview.tagged_other} transakcij bo izgubilo obstoječo oznako. Tega ni mogoče razveljaviti.`}
              />
            )}
          </Space>
        </Spin>
      )}
    </>
  );

  return (
    <>
      <Form layout="vertical" form={form} initialValues={{ is_active: true }}>
        <Drawer
          title={
            <Space>
              <span>{isCreation ? "Ustvari novo pravilo" : `Pravilo #${props.id}`}</span>
              {!isCreation && (
                <Segmented
                  size="small"
                  value={activeDrawerTab}
                  onChange={setActiveDrawerTab}
                  options={[
                    { label: 'Podatki', value: 'data' },
                    { label: 'Zgodovina', value: 'history' },
                  ]}
                />
              )}
            </Space>
          }
          width={720}
          onClose={handleCancel}
          open={props.open}
          extra={
            activeDrawerTab === 'data' && (
              <Space>
                <Button disabled={isCreation} onClick={() => setShowDeletionConfirmModal(true)} danger><DeleteOutlined /></Button>
                <Button onClick={handleCancel}><CloseOutlined /></Button>
                <Button onClick={onSubmit} type="primary" loading={applying}>Shrani</Button>
              </Space>
            )
          }
        >
          {activeDrawerTab === 'data' && (
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  name="pattern"
                  label='Vzorec (del naziva)'
                  extra='Ujema se, če naziv vsebuje ta niz. Velike in male črke ter šumniki niso pomembni.'
                  rules={[{ required: true, message: 'Prosim vnesite vzorec' }]}
                >
                  <Input placeholder={'npr. petrol'} />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="tag"
                  label='Oznaka'
                  rules={[{ required: true, message: 'Prosim izberite oznako' }]}
                >
                  {/* allowCreate (the default) keeps the "Ustvari novo oznako"
                      footer in the dropdown: writing a rule is exactly when you
                      notice the tag you want does not exist yet, and bouncing to
                      /tags would lose the half-filled rule. */}
                  <TagsSelect
                    multiple={false}
                    minWidth={0}
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="title_override"
                  label='Prepiši naziv (neobvezno)'
                  extra='Nerazumljiv bančni naziv zamenja s tem besedilom. Pri uporabi za obstoječe transakcije se zapiše samodejno; v predalu transakcije se ponudi kot "Preimenuj v ...", da ne prepiše naziva med tipkanjem. Ker se pravila ujemajo po nazivu, se po preimenovanju to pravilo na teh transakcijah ne ujema več.'
                >
                  <Input placeholder={'npr. Eurospin Nova Gorica'} />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item name="is_active" label='Aktivno' valuePropName="checked">
                  <Switch />
                </Form.Item>
              </Col>

              <Col span={24}>{previewSection}</Col>
            </Row>
          )}

          {activeDrawerTab === 'history' && (
            <HistoryTimeline model="TagRule" objectId={props.id} />
          )}
        </Drawer>
      </Form>

      <ConfirmModal
        textComponent={<p>{`Ste prepričani, da želite izbrisati pravilo »${data?.pattern}«?`}</p>}
        open={showDeletionConfirmModal}
        confirmBtnTitle='Izbriši'
        confirmBtnProps={{'danger': true}}
        onOk={() => handleConfirmedDelete()}
        onCancel={() => {
          setShowDeletionConfirmModal(false);
        }}
      />
    </>
  );
};

export default TagRuleDrawer;
